import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { REACT_APP_CHAOS_OVERLAY_DOWNLOAD_URL } from 'src/constants';
import { chaosEffectsWidgetApi } from 'src/api';
import { setAppTitle } from 'src/store/app';
import {
  bindChaosReward,
  createChaosTwitchReward,
  createManualChaosTwitchReward,
  fetchChaosActions,
  fetchChaosClientStatus,
  fetchChaosLogs,
  fetchTwitchRewards,
  syncChaosNewActions,
  syncChaosTwitchRewards,
  testChaosAction,
  updateChaosAction,
  chaosActionsSelector,
  chaosClientStatusSelector,
  chaosIsLoadingActionsSelector,
  chaosIsSyncingTwitchRewardsSelector,
  chaosLogsSelector,
  chaosRewardsSelector,
  chaosSyncReportSelector
} from 'src/store/chaos';

import './chaos.scss';

const { Text } = Typography;
const CHAOS_LOGS_POLL_INTERVAL_MS = 5000;

export const ChaosMode = () => {
  const { t } = useTranslation('chaos');
  const dispatch = useDispatch();
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [createRewardModalOpen, setCreateRewardModalOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [pendingTestActionIds, setPendingTestActionIds] = useState({});
  const [effectsWidget, setEffectsWidget] = useState(null);
  const [effectsWidgetLoading, setEffectsWidgetLoading] = useState(false);
  const [actionDrafts, setActionDrafts] = useState({});
  const [form] = Form.useForm();
  const [createRewardForm] = Form.useForm();

  const actions = useSelector(chaosActionsSelector);
  const rewards = useSelector(chaosRewardsSelector);
  const syncReportByActionKey = useSelector(chaosSyncReportSelector);
  const logs = useSelector(chaosLogsSelector);
  const clientStatus = useSelector(chaosClientStatusSelector);
  const isLoadingActions = useSelector(chaosIsLoadingActionsSelector);
  const isSyncingTwitchRewards = useSelector(chaosIsSyncingTwitchRewardsSelector);

  const inputTypeOptions = useMemo(
    () => [
      { label: t('inputTypes.keyboard'), value: 'keyboard' },
      { label: t('inputTypes.mouse'), value: 'mouse' },
      { label: t('inputTypes.overlay'), value: 'overlay' },
      { label: t('inputTypes.combo'), value: 'combo' }
    ],
    [t]
  );

  useEffect(() => {
    dispatch(setAppTitle(t('menu:chaos')));
    dispatch(fetchChaosActions());
    dispatch(fetchTwitchRewards());
    dispatch(fetchChaosClientStatus());
  }, [dispatch, t]);

  useEffect(() => {
    let active = true;
    chaosEffectsWidgetApi
      .get()
      .then((response) => active && setEffectsWidget(response?.data?.data || null))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const createEffectsWidget = async () => {
    setEffectsWidgetLoading(true);
    try {
      const response = await chaosEffectsWidgetApi.create();
      setEffectsWidget(response?.data?.data || null);
    } finally {
      setEffectsWidgetLoading(false);
    }
  };

  const toggleEffectsWidget = async (enabled) => {
    if (!effectsWidget?._id) return;
    const response = await chaosEffectsWidgetApi.update(effectsWidget._id, { enabled });
    setEffectsWidget(response?.data?.data || null);
  };

  const copyEffectsWidgetUrl = async () => {
    if (!effectsWidget?.url) return;
    await navigator.clipboard.writeText(effectsWidget.url);
  };

  useEffect(() => {
    dispatch(fetchChaosLogs());

    const intervalId = setInterval(() => {
      dispatch(fetchChaosLogs());
    }, CHAOS_LOGS_POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch]);

  const rewardsById = useMemo(() => {
    const map = new Map();
    rewards.forEach((reward) => {
      map.set(String(reward.id), reward);
    });
    return map;
  }, [rewards]);

  const saveActionAndSyncTwitch = async ({ id, payload }) => {
    const result = await dispatch(updateChaosAction({ id, payload })).unwrap();
    await dispatch(syncChaosTwitchRewards()).unwrap();
    return result;
  };

  const updateActionDraft = ({ id, payload }) => {
    const actionId = String(id || '');
    if (!actionId || !payload || typeof payload !== 'object') return;

    setActionDrafts((previous) => ({
      ...previous,
      [actionId]: { ...(previous[actionId] || {}), ...payload }
    }));
  };

  const saveActionDraftOnBlur = async ({ id, field }) => {
    const actionId = String(id || '');
    const value = actionDrafts[actionId]?.[field];
    if (!actionId || value === undefined) return;

    try {
      await saveActionAndSyncTwitch({ id: actionId, payload: { [field]: value } });
      setActionDrafts((previous) => {
        if (previous[actionId]?.[field] !== value) return previous;
        const nextFields = { ...previous[actionId] };
        delete nextFields[field];
        const next = { ...previous };
        if (Object.keys(nextFields).length) next[actionId] = nextFields;
        else delete next[actionId];
        return next;
      });
    } catch (_error) {
      // Keep the draft visible so the user can correct or retry it.
    }
  };

  const runTestAction = async (actionId) => {
    const id = String(actionId || '');
    if (!id) return;

    if (pendingTestActionIds[id]) return;

    setPendingTestActionIds((prev) => ({ ...prev, [id]: true }));

    try {
      await dispatch(testChaosAction({ id }));
    } finally {
      setPendingTestActionIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const parseKeysInput = (rawValue) =>
    String(rawValue || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) =>
        part
          .split('+')
          .map((keyPart) => keyPart.trim())
          .filter(Boolean)
          .join('+')
      );

  const actionColumns = [
    {
      title: t('table.action'),
      dataIndex: 'actionKey',
      key: 'actionKey',
      width: 140,
      render: (value) => <Text strong>{t(`actionNames.${value}`, { defaultValue: value })}</Text>
    },
    {
      title: t('table.enabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (enabled, row) => (
        <Switch
          checked={Boolean(enabled)}
          onChange={(checked) =>
            saveActionAndSyncTwitch({ id: row._id, payload: { enabled: checked } }).catch(() => {})
          }
        />
      )
    },
    {
      title: t('table.twitchReward'),
      dataIndex: 'rewardId',
      key: 'rewardId',
      width: 280,
      render: (rewardId, row) => {
        const selectedRewardId = rewardId || undefined;
        const rewardExists = rewardId ? rewardsById.has(String(rewardId)) : true;

        return (
          <Space direction="vertical" size={6}>
            <Select
              placeholder={t('placeholders.bindExistingReward')}
              value={selectedRewardId}
              allowClear
              style={{ width: 250 }}
              options={rewards.map((reward) => ({
                label: `${reward.title} (${reward.cost})`,
                value: reward.id
              }))}
              onChange={(value) => {
                dispatch(bindChaosReward({ id: row._id, rewardId: value || null }));
              }}
            />
            {!rewardExists ? <Tag color="red">{t('syncStatus.rewardNotFound')}</Tag> : null}
          </Space>
        );
      }
    },
    {
      title: t('table.cost'),
      dataIndex: 'cost',
      key: 'cost',
      width: 110,
      render: (cost, row) => (
        <InputNumber
          min={1}
          value={actionDrafts[String(row._id)]?.cost ?? cost}
          onChange={(value) => {
            if (value === null || value === undefined) return;
            updateActionDraft({ id: row._id, payload: { cost: value } });
          }}
          onBlur={() => saveActionDraftOnBlur({ id: row._id, field: 'cost' })}
        />
      )
    },
    {
      title: t('table.cooldownSec'),
      dataIndex: 'cooldownSec',
      key: 'cooldownSec',
      width: 140,
      render: (cooldownSec, row) => (
        <InputNumber
          min={0}
          value={actionDrafts[String(row._id)]?.cooldownSec ?? cooldownSec}
          onChange={(value) => {
            if (value === null || value === undefined) return;
            updateActionDraft({ id: row._id, payload: { cooldownSec: value } });
          }}
          onBlur={() => saveActionDraftOnBlur({ id: row._id, field: 'cooldownSec' })}
        />
      )
    },
    {
      title: t('table.inputEffect'),
      key: 'input',
      render: (_, row) => (
        <Space wrap>
          <Select
            style={{ width: 120 }}
            value={row.inputType}
            options={inputTypeOptions}
            disabled
          />
          <Input
            placeholder={t('placeholders.keys')}
            value={Array.isArray(row.keys) ? row.keys.join(',') : ''}
            style={{ width: 140 }}
            disabled
          />
          <Input
            placeholder={t('placeholders.mouseAction')}
            value={row.mouseAction || ''}
            style={{ width: 140 }}
            disabled
          />
          <InputNumber
            min={0}
            value={row.durationMs}
            placeholder={t('placeholders.durationMs')}
            disabled
          />
        </Space>
      )
    },
    {
      title: t('table.actions'),
      key: 'actions',
      width: 340,
      render: (_, row) => (
        <Space wrap>
          <Button
            loading={Boolean(pendingTestActionIds[String(row._id)])}
            disabled={Boolean(pendingTestActionIds[String(row._id)])}
            onClick={() => runTestAction(row._id)}
          >
            {t('actions.test')}
          </Button>
          <Button onClick={() => dispatch(createChaosTwitchReward({ id: row._id }))}>
            {t('actions.createReward')}
          </Button>
          <Button
            onClick={() => {
              setEditingAction(row);
              form.setFieldsValue({
                rewardTitle: row.rewardTitle,
                widgetColor: row.widgetColor || '#a970ff',
                cost: row.cost,
                cooldownSec: row.cooldownSec,
                inputType: row.inputType,
                keys: Array.isArray(row.keys) ? row.keys.join(',') : '',
                mouseAction: row.mouseAction || '',
                durationMs: row.durationMs
              });
              setManualModalOpen(true);
            }}
          >
            {t('actions.manualSetup')}
          </Button>
          <Popconfirm
            title={t('actions.unbindReward')}
            description={t('actions.unbindRewardConfirm')}
            onConfirm={() => dispatch(bindChaosReward({ id: row._id, rewardId: null }))}
          >
            <Button danger>{t('actions.unbind')}</Button>
          </Popconfirm>
        </Space>
      )
    },
    {
      title: t('table.status'),
      key: 'syncStatus',
      width: 170,
      render: (_, row) => {
        const report = syncReportByActionKey?.[row.actionKey];

        if (!row.rewardId) {
          return <Tag>{t('syncStatus.notBound')}</Tag>;
        }

        if (!rewardsById.has(String(row.rewardId))) {
          return <Tag color="red">{t('syncStatus.rewardNotFound')}</Tag>;
        }

        if (!report?.status) {
          return <Tag color="blue">{t('syncStatus.bound')}</Tag>;
        }

        if (report.status === 'updated') {
          return <Tag color="gold">{t('syncStatus.updated')}</Tag>;
        }

        if (report.status === 'ok') {
          return <Tag color="green">{t('syncStatus.ok')}</Tag>;
        }

        if (report.status === 'not_bound') {
          return <Tag>{t('syncStatus.notBound')}</Tag>;
        }

        if (report.status === 'reward_not_found_on_twitch') {
          return <Tag color="red">{t('syncStatus.rewardNotFound')}</Tag>;
        }

        return <Tag>{report.status}</Tag>;
      }
    }
  ];

  const logColumns = [
    {
      title: t('logs.viewer'),
      dataIndex: 'viewerName',
      key: 'viewerName',
      width: 140,
      render: (viewerName) => viewerName || t('common.notAvailable')
    },
    {
      title: t('logs.reward'),
      dataIndex: 'rewardTitle',
      key: 'rewardTitle'
    },
    {
      title: t('logs.action'),
      dataIndex: 'actionKey',
      key: 'actionKey',
      width: 140,
      render: (value) => t(`actionNames.${value}`, { defaultValue: value })
    },
    {
      title: t('logs.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const color =
          status === 'success'
            ? 'green'
            : status === 'queued'
              ? 'blue'
              : status === 'error'
                ? 'red'
                : 'orange';

        const labelByStatus = {
          success: t('logStatus.success'),
          queued: t('logStatus.queued'),
          error: t('logStatus.error')
        };

        return <Tag color={color}>{labelByStatus[status] || status}</Tag>;
      }
    },
    {
      title: t('logs.time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (createdAt) =>
        createdAt ? dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss') : t('common.notAvailable')
    },
    {
      title: t('logs.message'),
      dataIndex: 'message',
      key: 'message'
    }
  ];

  return (
    <div className="chaos-mode">
      <div className="chaos-mode__content">
        <Alert type="warning" showIcon message={t('warning')} />

        <Card className="chaos-mode__card" title={t('cards.status')}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label={t('status.chaosModeLabel')}>
              {t('status.enabled')}
            </Descriptions.Item>
            <Descriptions.Item label={t('status.twitchLabel')}>
              {t('status.connected')}
            </Descriptions.Item>
            <Descriptions.Item label={t('status.localClientStatusLabel')}>
              <Tag color={clientStatus?.status === 'online' ? 'green' : 'red'}>
                {clientStatus?.status || t('status.offline')}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('status.deviceLabel')}>
              {clientStatus?.deviceName || t('common.notAvailable')}
            </Descriptions.Item>
            <Descriptions.Item label={t('status.lastSeenLabel')}>
              {clientStatus?.lastSeenAt
                ? dayjs(clientStatus.lastSeenAt).format('YYYY-MM-DD HH:mm:ss')
                : t('common.notAvailable')}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="chaos-mode__card" title={t('download.title')}>
          <div className="chaos-mode__download">
            <Text className="chaos-mode__download-description">{t('download.description')}</Text>
            <Text type="secondary">{t('download.meta')}</Text>
            <Button
              className="chaos-mode__download-button"
              type="primary"
              href={REACT_APP_CHAOS_OVERLAY_DOWNLOAD_URL}
              target="_blank"
              rel="noreferrer"
            >
              {t('download.cta')}
            </Button>
          </div>
        </Card>

        <Card className="chaos-mode__card" title="OBS Visual Effects">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text>Добавьте этот Browser Source поверх Game Capture, чтобы зрители видели визуальные эффекты Chaos Mode.</Text>
            {!effectsWidget ? (
              <Button type="primary" loading={effectsWidgetLoading} onClick={createEffectsWidget}>Создать виджет</Button>
            ) : (
              <>
                <Space wrap>
                  <Switch checked={Boolean(effectsWidget.enabled)} onChange={toggleEffectsWidget} />
                  <Text>{effectsWidget.enabled ? 'Виджет включен' : 'Виджет выключен'}</Text>
                </Space>
                <Text strong>{effectsWidget.url}</Text>
                <Button onClick={copyEffectsWidgetUrl}>Скопировать ссылку</Button>
                <Text type="secondary">Размер Browser Source: 1920 × 1080</Text>
                <Text type="secondary">1. OBS → Sources. 2. Add Browser Source. 3. Paste widget URL. 4. Width: 1920. 5. Height: 1080. 6. Put it above CS2 Game Capture.</Text>
              </>
            )}
          </Space>
        </Card>

        <Card
          className="chaos-mode__card"
          title={t('cards.rewardsAndActions')}
          extra={
            <Space>
              <Button onClick={() => dispatch(syncChaosNewActions())}>
                {t('actions.syncNew')}
              </Button>
              <Button
                loading={isSyncingTwitchRewards}
                disabled={isSyncingTwitchRewards}
                onClick={() => dispatch(syncChaosTwitchRewards())}
              >
                {t('actions.syncTwitchRewards')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  createRewardForm.setFieldsValue({
                    title: '',
                    cost: 100,
                    cooldownSec: 0,
                    isEnabled: true
                  });
                  setCreateRewardModalOpen(true);
                }}
              >
                {t('actions.createStandaloneReward')}
              </Button>
              <Button onClick={() => dispatch(fetchTwitchRewards())}>
                {t('actions.refreshRewards')}
              </Button>
            </Space>
          }
        >
          <Table
            rowKey={(row) => row._id}
            columns={actionColumns}
            dataSource={actions}
            loading={isLoadingActions || isSyncingTwitchRewards}
            pagination={false}
            scroll={{ x: 1400 }}
          />
        </Card>

        <Card className="chaos-mode__card" title={t('cards.logs')}>
          <Table
            rowKey={(row) => row._id}
            columns={logColumns}
            dataSource={logs}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </div>

      <Modal
        title={t('modal.title', {
          actionKey: t(`actionNames.${editingAction?.actionKey}`, {
            defaultValue: editingAction?.actionKey || ''
          })
        })}
        open={manualModalOpen}
        onCancel={() => setManualModalOpen(false)}
        onOk={async () => {
          const values = await form.validateFields();

          const keys = parseKeysInput(values.keys);

          await saveActionAndSyncTwitch({
            id: editingAction._id,
            payload: {
              rewardTitle: values.rewardTitle,
              widgetColor: values.widgetColor || '#a970ff',
              cost: values.cost,
              cooldownSec: values.cooldownSec,
              inputType: values.inputType,
              keys,
              mouseAction: values.mouseAction || null,
              durationMs: values.durationMs ?? null
            }
          });

          setManualModalOpen(false);
          setEditingAction(null);
        }}
        confirmLoading={isSyncingTwitchRewards}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rewardTitle"
            label={t('modal.fields.rewardTitle')}
            rules={[{ required: true, message: t('modal.errors.required') }]}
          >
            <Input maxLength={45} />
          </Form.Item>

          <Form.Item name="widgetColor" label="Цвет Chaos Widget" initialValue="#a970ff">
            <input className="chaos-mode__color-picker" type="color" />
          </Form.Item>

          <Form.Item
            name="cost"
            label={t('modal.fields.cost')}
            rules={[{ required: true, message: t('modal.errors.required') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="cooldownSec"
            label={t('modal.fields.cooldownSec')}
            rules={[{ required: true, message: t('modal.errors.required') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="inputType"
            label={t('modal.fields.inputType')}
            rules={[{ required: true, message: t('modal.errors.required') }]}
          >
            <Select options={inputTypeOptions} />
          </Form.Item>

          <Form.Item name="keys" label={t('modal.fields.keys')}>
            <Input placeholder={t('placeholders.keys')} />
          </Form.Item>

          <Form.Item name="mouseAction" label={t('modal.fields.mouseAction')}>
            <Input placeholder={t('modal.placeholders.mouseAction')} />
          </Form.Item>

          <Form.Item name="durationMs" label={t('modal.fields.durationMs')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('standaloneRewardModal.title')}
        open={createRewardModalOpen}
        onCancel={() => setCreateRewardModalOpen(false)}
        onOk={async () => {
          const values = await createRewardForm.validateFields();

          await dispatch(
            createManualChaosTwitchReward({
              title: values.title,
              cost: values.cost,
              cooldownSec: values.cooldownSec,
              isEnabled: values.isEnabled
            })
          );

          setCreateRewardModalOpen(false);
        }}
      >
        <Form form={createRewardForm} layout="vertical">
          <Form.Item
            name="title"
            label={t('standaloneRewardModal.fields.title')}
            rules={[{ required: true, message: t('standaloneRewardModal.errors.required') }]}
          >
            <Input maxLength={45} placeholder={t('standaloneRewardModal.placeholders.title')} />
          </Form.Item>

          <Form.Item
            name="cost"
            label={t('standaloneRewardModal.fields.cost')}
            rules={[{ required: true, message: t('standaloneRewardModal.errors.required') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="cooldownSec"
            label={t('standaloneRewardModal.fields.cooldownSec')}
            rules={[{ required: true, message: t('standaloneRewardModal.errors.required') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="isEnabled" valuePropName="checked">
            <Switch />
            <Text style={{ marginLeft: 8 }}>{t('standaloneRewardModal.fields.enabled')}</Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
